import { type IThemePalette } from "../types";

export interface ISelectorSearchTokens {
  inputHeight: number;
  fontSize: number;
  paddingHorizontal: number;
  paddingVertical: number;
  iconColor: string;
  clearIconColor: string;
}

export interface ISelectorRowTokens {
  restingBg: string;
  hoverBg: string;
  selectedBg: string;
  restingBorderColor: string;
  hoverBorderColor: string;
  selectedBorderColor: string;
  titleColor: string;
  subtitleColor: string;
  borderRadius: number;
  padding: number;
}

export interface ISelectorBadgeTokens {
  size: number;
  borderRadius: number;
  contactBorderRadius: number;
  backgroundColor: string;
  textColor: string;
  iconColor: string;
}

export interface ISelectorCreateRowTokens {
  backgroundColor: string;
  borderColor: string;
  borderRadius: number;
  iconCircleBg: string;
  iconCircleColor: string;
  titleColor: string;
  subtitleColor: string;
}

export interface ISelectorEmptyTokens {
  iconSize: number;
  iconColor: string;
  textColor: string;
  fontSize: number;
  paddingVertical: number;
}

export interface ISelectorFooterTokens {
  cancelVariant: "secondary";
  doneVariant: "primary";
  gap: number;
}

export interface ISelectorTheme {
  searchBar: ISelectorSearchTokens;
  row: ISelectorRowTokens;
  avatarBadge: ISelectorBadgeTokens;
  createRow: ISelectorCreateRowTokens;
  emptyState: ISelectorEmptyTokens;
  footer: ISelectorFooterTokens;
}

/**
 * Pure factory generating the entity selector component theme.
 * Standardizes ContactSelector, GroupSelector, MemorySelector, MediaSelector, TagSelector, and ThemedSelector.
 *
 * @param palette - The active semantic theme palette.
 * @param isDark - Whether dark mode is currently active.
 * @returns Fully resolved ISelectorTheme.
 */
export function getSelectorTheme(palette: IThemePalette, isDark: boolean): ISelectorTheme {
  return {
    searchBar: {
      inputHeight: 32,
      fontSize: 12,
      paddingHorizontal: 8,
      paddingVertical: 4,
      iconColor: palette.tint,
      clearIconColor: palette.textMuted,
    },
    row: {
      restingBg: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(255, 255, 255, 0.72)",
      hoverBg: palette.buttonSecondary,
      selectedBg: isDark ? "rgba(143, 162, 207, 0.18)" : "rgba(255, 255, 255, 0.95)",
      restingBorderColor: palette.border,
      hoverBorderColor: palette.buttonHoverBorder,
      selectedBorderColor: palette.tint,
      titleColor: palette.text,
      subtitleColor: palette.textMuted,
      borderRadius: 6,
      padding: 6,
    },
    avatarBadge: {
      size: 28,
      borderRadius: 6,
      contactBorderRadius: 14,
      backgroundColor: palette.buttonSecondary,
      textColor: palette.text,
      iconColor: palette.tint,
    },
    createRow: {
      backgroundColor: palette.buttonSecondary,
      borderColor: palette.tint,
      borderRadius: 6,
      iconCircleBg: palette.tint,
      iconCircleColor: "#FFFFFF",
      titleColor: palette.tint,
      subtitleColor: palette.textMuted,
    },
    emptyState: {
      iconSize: 32,
      iconColor: palette.textMuted,
      textColor: palette.textMuted,
      fontSize: 12,
      paddingVertical: 24,
    },
    footer: {
      cancelVariant: "secondary",
      doneVariant: "primary",
      gap: 8,
    },
  };
}
